interface FrontMatter {
  title: string;
  date: string;
  author: string;
  category: string;
  excerpt: string;
  image: string;
}

export interface BlogPost extends FrontMatter {
  slug: string;
  content: string;
}

const parseFrontMatter = (content: string): { data: FrontMatter; content: string } => {
  try {
    // More robust regex that handles different line endings and whitespace
    const frontMatterRegex = /^---[\r\n]+([\s\S]*?)[\r\n]+---[\r\n]+([\s\S]*)$/;
    const matches = content.match(frontMatterRegex);

    if (!matches) {
      throw new Error('Invalid front matter format');
    }

    const [, frontMatter, markdownContent] = matches;
    const data: Partial<FrontMatter> = {};

    // Parse each line of front matter
    frontMatter.split(/[\r\n]+/).forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        // Find the first colon that's not part of a URL
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex !== -1) {
          const key = trimmedLine.slice(0, colonIndex).trim();
          const value = trimmedLine.slice(colonIndex + 1).trim();
          if (key && value) {
            data[key as keyof FrontMatter] = value;
          }
        }
      }
    });

    // Validate required fields
    const requiredFields: (keyof FrontMatter)[] = ['title', 'date', 'author', 'category', 'excerpt', 'image'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required front matter fields: ${missingFields.join(', ')}`);
    }

    return {
      data: data as FrontMatter,
      content: markdownContent.trim()
    };
  } catch (error) {
    console.error('Error parsing front matter:', error);
    throw error;
  }
};

export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const blogFiles = import.meta.glob('../blogs/*.md', { 
      query: '?raw',
      import: 'default',
      eager: true 
    });
    const posts: BlogPost[] = [];

    for (const path in blogFiles) {
      try {
        const slug = path.replace('../blogs/', '').replace('.md', '');
        const fileContent = blogFiles[path] as string;
        const { data, content } = parseFrontMatter(fileContent);

        posts.push({
          slug,
          ...data,
          content
        });
      } catch (error) {
        console.error(`Error processing blog post ${path}:`, error);
      }
    }

    if (posts.length === 0) {
      throw new Error('No valid blog posts found');
    }

    // Sort posts by date in descending order
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.error('Error getting blog posts:', error);
    throw error;
  }
};

export const getBlogPost = async (slug: string): Promise<BlogPost | null> => {
  try {
    const blogFiles = import.meta.glob('../blogs/*.md', { 
      query: '?raw',
      import: 'default',
      eager: true 
    });
    const path = `../blogs/${slug}.md`;

    if (!(path in blogFiles)) {
      return null;
    }

    const fileContent = blogFiles[path] as string;
    const { data, content } = parseFrontMatter(fileContent);

    return {
      slug,
      ...data,
      content
    };
  } catch (error) {
    console.error('Error getting blog post:', error);
    return null;
  }
}; 